/**
 * Spoks Back-in-Stock Subscription
 *
 * Submits a customer's contact info to the Spoks webhook so they can be
 * notified when an out-of-stock product variant is replenished.
 *
 * Usage: called by the back-in-stock-form.liquid snippet on form submit.
 */

const SPOKS_WEBHOOK_URL =
  "https://webhook.spoks.com/c7d06e79-7c9d-42f0-b1c2-d9832b028145";

/**
 * Remove HTML tags and trim a string so no markup reaches the webhook.
 */
function bisStripTags(str) {
  return str.replace(/<[^>]*>/g, "").trim();
}

/**
 * Validate form fields. Returns an error message string, or null if valid.
 *
 * Rules:
 *   - Name: required, letters/spaces/hyphens/apostrophes only, 1–100 chars
 *   - Email: required, must match standard email format
 *   - Phone: optional, but if provided must contain only digits, spaces,
 *            parentheses, hyphens, plus signs, or dots (7–20 chars)
 */
function bisValidate(name, email, phone) {
  if (!name) return "Please enter your name.";
  if (!/^[\p{L}\s'\-]{1,100}$/u.test(name))
    return "Name contains invalid characters.";

  if (!email) return "Please enter your email address.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email))
    return "Please enter a valid email address.";

  if (phone && !/^[\d\s()\-+.]{7,20}$/.test(phone))
    return "Please enter a valid phone number (digits, spaces, dashes, and parentheses only).";

  return null;
}

/**
 * Subscribe a customer to a back-in-stock notification.
 *
 * @param {Object} params
 * @param {string} params.name          - Customer's full name
 * @param {string} params.email         - Customer's email address
 * @param {string} params.phone         - Customer's phone number (optional)
 * @param {string} params.productHandle - Shopify product handle (used to build the tag)
 * @param {string} params.variantId        - Shopify variant ID (appended to tag for specificity)
 * @param {string} [params.originatingForm] - Identifies which form on the page submitted (optional)
 * @returns {Promise<void>}
 */
async function spokSubscribeBackInStock({
  name,
  email,
  phone = "",
  productHandle,
  variantId,
  originatingForm = "",
}) {
  const tags = [
    `back-in-stock_${productHandle}_${variantId}`,
    originatingForm,
  ]
    .filter(Boolean)
    .join(", ");

  const response = await fetch(SPOKS_WEBHOOK_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, phone, tags }),
  });

  if (!response.ok) {
    throw new Error(`Spoks webhook failed: ${response.status}`);
  }
}

/**
 * Wire up all back-in-stock forms on the page.
 * Forms must have data-product-handle and data-variant-id attributes.
 */
document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll(".js-back-in-stock-form").forEach(function (form) {
    form.addEventListener("submit", async function (e) {
      e.preventDefault();
      // Honeypot check — reject if filled
      if (form.querySelector('[name="website"]').value !== '') {
        return;
      }

      // Timing check — reject if submitted in under 3 seconds
      const loadedAt = parseInt(form.querySelector('[name="form_loaded_at"]').value, 10);
      if (Date.now() - loadedAt < 3000) {
        return;
      }


      const submitBtn = form.querySelector('[type="submit"]');
      const successMsg = form.querySelector(".bis-success");
      const errorMsg = form.querySelector(".bis-error");

      if (successMsg) successMsg.hidden = true;
      if (errorMsg) errorMsg.hidden = true;

      const name  = bisStripTags(form.querySelector('[name="name"]').value);
      const email = bisStripTags(form.querySelector('[name="email"]').value);
      const phone = bisStripTags((form.querySelector('[name="phone"]') || {}).value || "");

      const validationError = bisValidate(name, email, phone);
      if (validationError) {
        if (errorMsg) {
          errorMsg.textContent = validationError;
          errorMsg.hidden = false;
        }
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = "Submitting…";

      try {
        await spokSubscribeBackInStock({
          name,
          email,
          phone,
          productHandle: form.dataset.productHandle,
          variantId: form.dataset.variantId,
          originatingForm: form.dataset.originatingForm || "",
        });

        form.reset();
        if (successMsg) successMsg.hidden = false;
        submitBtn.textContent = "Done";
      } catch (err) {
        console.error(err);
        if (errorMsg) {
          errorMsg.textContent = "Uh oh... this is a little embarrassing, but it appears something went wrong. Please try again or contact us directly.";
          errorMsg.hidden = false;
        }
        submitBtn.disabled = false;
        submitBtn.textContent = "Notify Me";
      }
    });
  });
});
